// PersonnelEditWrapper.tsx - Form-based editing wrapper for personnel records
import React, { useEffect, useState, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

// Enhanced interface with multiple document types (same as PersonnelView)
interface PersonnelRecord {
  personnelId: number;
  companyId: number;
  fullName: string;
  firstName?: string;
  lastName?: string;
  nationality?: string;
  passportNumber?: string;
  visaType?: string;
  dateOfBirth?: string;
  sex?: string;
  placeOfBirth?: string;
  nationalIdNo?: string;
  passportIssueDate?: string;
  passportExpiryDate?: string;
  typeOfVisa?: string;
  visaIssueDate?: string;
  visaExpiry?: string;
  bloodGroup?: string;
  photoUrl?: string;
  
  // Legacy document fields (for backward compatibility)
  documentFileName?: string;
  documentFileUrl?: string;
  documentFileSize?: number;
  documentFileType?: string;
  
  // NEW: Passport document fields
  passportDocumentFileName?: string;
  passportDocumentFileUrl?: string;
  passportDocumentFileSize?: number;
  passportDocumentFileType?: string;
  
  // NEW: Visa/ID document fields
  visaIdDocumentFileName?: string;
  visaIdDocumentFileUrl?: string;
  visaIdDocumentFileSize?: number;
  visaIdDocumentFileType?: string;
  
  createdAt: string;
  updatedAt: string;
}

interface StatusUpdate {
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
}

interface PersonnelEditWrapperProps {
  personnelData: PersonnelRecord | null;
  onClose: () => void;
  onSave: (updatedData: PersonnelRecord) => void;
  getAccessToken: () => Promise<string>;
  onStatusUpdate?: (status: StatusUpdate) => void;
}

interface DocumentInfo {
  type: 'passport' | 'visaid' | 'legacy';
  label: string;
  fileName?: string;
  fileUrl?: string;
  fileSize?: number;
  fileType?: string;
  icon: string;
  badgeColor: string;
}

export const PersonnelEditWrapper: React.FC<PersonnelEditWrapperProps> = ({
  personnelData,
  onClose,
  onSave,
  getAccessToken,
  onStatusUpdate
}) => {
  const [formData, setFormData] = useState<Partial<PersonnelRecord>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [profilePhotoFile, setProfilePhotoFile] = useState<File | null>(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState<string | null>(null);

  // CSS animation keyframes as a style element in the document head
  useEffect(() => {
    const styleId = 'personnel-edit-spinner-styles';
    
    // Check if styles already exist
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .spinner {
          animation: spin 1s linear infinite;
        }
      `;
      document.head.appendChild(style);
    }

    // Cleanup function to remove styles when component unmounts
    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);

  // Initialize form data
  useEffect(() => {
    if (personnelData) {
      setFormData({ ...personnelData });
    }
  }, [personnelData]);

  // Helper functions (same as PersonnelView)
  

  const constructDocumentUrl = (documentUrl: string | undefined): string | undefined => {
    if (!documentUrl) return undefined;
    
    if (documentUrl.startsWith('http://') || documentUrl.startsWith('https://')) {
      return documentUrl;
    }
    
    if (documentUrl.startsWith('/api/')) {
      return `${API_BASE_URL.replace('/api', '')}${documentUrl}`;
    }
    
    if (documentUrl.startsWith('/')) {
      return `${API_BASE_URL.replace('/api', '')}${documentUrl}`;
    }
    
    return `${API_BASE_URL}/uploads/documents/${documentUrl}`;
  };



  const getAvailableDocuments = useCallback((): DocumentInfo[] => {
    if (!personnelData) return [];
    
    const documents: DocumentInfo[] = [];

    // Check for passport document
    if (personnelData.passportDocumentFileName) {
      documents.push({
        type: 'passport',
        label: 'Passport Document',
        fileName: personnelData.passportDocumentFileName,
        fileUrl: personnelData.passportDocumentFileUrl,
        fileSize: personnelData.passportDocumentFileSize,
        fileType: personnelData.passportDocumentFileType,
        icon: '',
        badgeColor: '#6b7280'
      });
    }

    // Check for visa/ID document
    if (personnelData.visaIdDocumentFileName) {
      documents.push({
        type: 'visaid',
        label: 'Visa/ID Document',
        fileName: personnelData.visaIdDocumentFileName,
        fileUrl: personnelData.visaIdDocumentFileUrl,
        fileSize: personnelData.visaIdDocumentFileSize,
        fileType: personnelData.visaIdDocumentFileType,
        icon: '',
        badgeColor: '#6b7280'
      });
    }

    // Check for legacy document (only if no new documents exist)
    if (personnelData.documentFileName && documents.length === 0) {
      documents.push({
        type: 'legacy',
        label: 'Document',
        fileName: personnelData.documentFileName,
        fileUrl: personnelData.documentFileUrl,
        fileSize: personnelData.documentFileSize,
        fileType: personnelData.documentFileType,
        icon: '',
        badgeColor: '#6b7280'
      });
    }

    return documents;
  }, [personnelData]);

  const getDocumentDownloadUrl = (document: DocumentInfo): string => {
    if (document.fileUrl) {
      return constructDocumentUrl(document.fileUrl) || '#';
    }
    
    if (document.type === 'legacy') {
      return `${API_BASE_URL}/personnel/${personnelData?.personnelId}/file/document`;
    }
    
    return `${API_BASE_URL}/personnel/${personnelData?.personnelId}/file/${document.type}`;
  };

  // Handle field changes
  const handleFieldChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
    
    // Auto-update fullName if first/last name changes
    if (field === 'firstName' || field === 'lastName') {
      const firstName = field === 'firstName' ? value : formData.firstName || '';
      const lastName = field === 'lastName' ? value : formData.lastName || '';
      if (firstName && lastName) {
        setFormData(prev => ({ ...prev, fullName: `${firstName} ${lastName}` }));
      }
    }
  };

  // Handle profile photo change
  const handleProfilePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        onStatusUpdate?.({
          type: 'error',
          message: 'Profile picture must be an image file (JPEG, PNG, GIF)'
        });
        return;
      }

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        onStatusUpdate?.({
          type: 'error',
          message: 'File size must be less than 10MB'
        });
        return;
      }

      setProfilePhotoFile(file);
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setProfilePhotoPreview(previewUrl);
      
      onStatusUpdate?.({
        type: 'success',
        message: 'Profile photo selected successfully'
      });
    }
  };

  // Validate form — all mandatory fields must be filled before saving
  const MANDATORY_FIELDS: { key: string; label: string }[] = [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'sex', label: 'Gender' },
    { key: 'dateOfBirth', label: 'Date of Birth' },
    { key: 'placeOfBirth', label: 'Place of Birth' },
    { key: 'nationality', label: 'Nationality' },
    { key: 'nationalIdNo', label: 'National ID Number' },
    { key: 'passportNumber', label: 'Passport Number' },
    { key: 'passportIssueDate', label: 'Passport Issue Date' },
    { key: 'passportExpiryDate', label: 'Passport Expiry Date' },
    { key: 'typeOfVisa', label: 'Visa Type' },
    { key: 'visaIssueDate', label: 'Visa Issue Date' },
    { key: 'visaExpiry', label: 'Visa Expiry Date' },
  ];

  const validateForm = (): Record<string, string> | null => {
    const errors: Record<string, string> = {};

    MANDATORY_FIELDS.forEach(({ key, label }) => {
      const fieldValue = formData[key as keyof PersonnelRecord];
      if (!fieldValue || fieldValue.toString().trim() === '') {
        errors[key] = `${label} is required`;
      }
    });

    // Ensure fullName stays in sync — derive from first + last if both present
    if (!errors['firstName'] && !errors['lastName']) {
      const first = (formData.firstName || '').trim();
      const last = (formData.lastName || '').trim();
      if (first && last && (!formData.fullName || formData.fullName.trim() === '')) {
        setFormData(prev => ({ ...prev, fullName: `${first} ${last}` }));
      }
    }

    // Also guard against empty fullName as a final check
    if (!formData.fullName || formData.fullName.trim() === '') {
      errors['fullName'] = 'Full Name is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length > 0 ? errors : null;
  };

  // Save changes
  const handleSave = async () => {
    const errors = validateForm();
    if (errors) {
      const missingCount = Object.keys(errors).length;
      const missingNames = Object.values(errors).slice(0, 3).map(v => v.replace(' is required', '')).join(', ');
      const suffix = missingCount > 3 ? ` and ${missingCount - 3} more` : '';
      const errorMsg = `Please fill in all required fields: ${missingNames}${suffix}`;
      setFormError(errorMsg);
      onStatusUpdate?.({
        type: 'error',
        message: errorMsg
      });

      // Scroll the first error field into view
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        setTimeout(() => {
          const el = document.querySelector(`[data-field="${firstErrorKey}"]`) as HTMLElement;
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
      }
      return;
    }

    setIsSaving(true);
    setFormError(null);
    
    try {
      const accessToken = await getAccessToken();
      const submitFormData = new FormData();

      // Add all text form data
      Object.entries(formData).forEach(([key, value]) => {
        if (value && value.toString().trim() !== '') {
          submitFormData.append(key, value.toString());
        }
      });

      // Add profile photo if changed
      if (profilePhotoFile) {
        submitFormData.append('photoFile', profilePhotoFile);
      }

      const response = await fetch(`${API_BASE_URL}/personnelupdate/${personnelData?.personnelId}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${accessToken}`
        },
        body: submitFormData
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          onStatusUpdate?.({
            type: 'success',
            message: `Personnel record updated successfully!`
          });
          
          // Call onSave with updated data
          onSave(result.data || { ...personnelData, ...formData });
          
          setTimeout(() => {
            onClose();
          }, 1500);
        } else {
          throw new Error(result.error || 'Failed to update record');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update record');
      }
    } catch (error) {
      const errorMsg = `Update failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
      setFormError(errorMsg);
      onStatusUpdate?.({
        type: 'error',
        message: errorMsg
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Format date for input display
  const formatDateForInput = (dateString: string | undefined): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  // Format date for display
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'Not provided';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // ─── Shared field styles ────────────────────────────────────────
  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '500',
    color: '#374151',
    fontSize: '14px',
    fontFamily: "'Segoe UI', sans-serif",
  };

  const fieldControlStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: "'Segoe UI', sans-serif",
    transition: 'border-color 0.2s, box-shadow 0.2s',
    backgroundColor: 'white',
    color: '#111827',
    boxSizing: 'border-box' as const,
    height: '46px',
  };

  // Render editable field
  const renderEditableField = (
    label: string, 
    field: string,
    type: 'text' | 'date' | 'select' = 'text',
    options?: string[],
    required: boolean = false
  ) => {
    const fieldValue = formData[field as keyof PersonnelRecord];
    const value = type === 'date' 
      ? formatDateForInput(fieldValue?.toString()) 
      : (fieldValue?.toString() || '');
    const hasError = validationErrors[field];

    if (type === 'select') {
      return (
        <div data-field={field}>
          <label style={labelStyle}>
            {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
          </label>
          <select
            value={value}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            style={{
              ...fieldControlStyle,
              borderColor: hasError ? '#ef4444' : '#d1d5db',
              color: value ? '#111827' : '#9ca3af',
              appearance: 'none' as const,
              WebkitAppearance: 'none' as const,
              MozAppearance: 'none' as const,
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 14px center',
              paddingRight: '38px',
              cursor: 'pointer',
            }}
          >
            <option value="" style={{ color: '#9ca3af' }}>Select...</option>
            {options?.map(option => (
              <option key={option} value={option} style={{ color: '#111827' }}>
                {option}
              </option>
            ))}
          </select>
          {hasError && (
            <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
              {hasError}
            </div>
          )}
        </div>
      );
    }

    return (
      <div data-field={field}>
        <label style={labelStyle}>
          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
        </label>
        <input
          type={type}
          value={value}
          onChange={(e) => handleFieldChange(field, e.target.value)}
          placeholder={type === 'text' ? `Enter ${label.toLowerCase()}` : ''}
          style={{
            ...fieldControlStyle,
            borderColor: hasError ? '#ef4444' : '#d1d5db',
            color: value ? '#111827' : '#9ca3af',
          }}
        />
        {hasError && (
          <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
            {hasError}
          </div>
        )}
      </div>
    );
  };

  // SVG icon helper for document types
  const getDocumentIcon = (type: string) => {
    if (type === 'passport') {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="3" width="20" height="18" rx="2" />
          <circle cx="12" cy="11" r="3" />
          <path d="M7 18h10" />
        </svg>
      );
    }
    if (type === 'visaid') {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <circle cx="8" cy="12" r="2.5" />
          <line x1="14" y1="10" x2="20" y2="10" />
          <line x1="14" y1="14" x2="20" y2="14" />
        </svg>
      );
    }
    // legacy / generic
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    );
  };

  // Render documents section (read-only in edit mode)
  const renderDocumentsSection = () => {
    const availableDocuments = getAvailableDocuments();

    if (availableDocuments.length === 0) {
      return (
        <div style={{
          marginBottom: '32px',
          padding: '20px',
          backgroundColor: '#fafafa',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <h4 style={{ 
            margin: '0 0 16px 0', 
            color: '#111827',
            fontSize: '16px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontFamily: "'Segoe UI', sans-serif",
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            Attached Documents
            <span style={{ 
              fontSize: '12px', 
              color: '#9ca3af', 
              fontWeight: 'normal',
              marginLeft: '8px'
            }}>
              (No documents found)
            </span>
          </h4>
          
          <div style={{
            padding: '16px',
            backgroundColor: '#f3f4f6',
            borderRadius: '6px',
            border: '1px solid #e5e7eb',
            fontSize: '13px',
            color: '#6b7280'
          }}>
            <p style={{ margin: '0', fontWeight: '500' }}>
              No documents attached to this personnel record.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div style={{
        marginBottom: '32px',
        padding: '20px',
        backgroundColor: '#fafafa',
        borderRadius: '8px',
        border: '1px solid #e5e7eb'
      }}>
        <h4 style={{ 
          margin: '0 0 16px 0', 
          color: '#111827',
          fontSize: '16px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontFamily: "'Segoe UI', sans-serif",
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
          </svg>
          Documents
          <span style={{ 
            fontSize: '12px', 
            color: '#9ca3af', 
            fontWeight: 'normal',
            marginLeft: '8px'
          }}>
            ({availableDocuments.length} document{availableDocuments.length === 1 ? '' : 's'})
          </span>
        </h4>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: availableDocuments.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '12px'
        }}>
          {availableDocuments.map((document, index) => (
            <div key={`${document.type}-${index}`} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              padding: '14px 16px',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
            }}>
              <div style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
                backgroundColor: '#f3f4f6',
                borderRadius: '8px',
              }}>
                {getDocumentIcon(document.type)}
              </div>
              
              <div style={{ flex: 1 }}>
                <span style={{
                  display: 'inline-block',
                  fontSize: '11px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  textTransform: 'uppercase' as const,
                  fontWeight: '600',
                  letterSpacing: '0.03em',
                  marginBottom: '6px',
                  fontFamily: "'Segoe UI', sans-serif",
                }}>
                  {document.type === 'visaid' ? 'VISA/ID' : document.type.toUpperCase()}
                </span>
                
                <div>
                  <a
                    href={getDocumentDownloadUrl(document)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#6b7280',
                      textDecoration: 'none',
                      transition: 'color 0.2s',
                      fontFamily: "'Segoe UI', sans-serif",
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    View / Download
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };



const constructPhotoUrl = (photoUrl: string | undefined): string | null => {
  if (!photoUrl) return null;
  
  if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://')) {
    return photoUrl;
  }
  
  if (photoUrl.startsWith('/api/')) {
    return `${API_BASE_URL.replace('/api', '')}${photoUrl}`;
  }
  
  if (photoUrl.startsWith('/')) {
    return `${API_BASE_URL.replace('/api', '')}${photoUrl}`;
  }
  
  return `${API_BASE_URL}/uploads/photos/${photoUrl}`;
};

// Replace the PhotoComponent in PersonnelEditWrapper.tsx with this:
const PhotoComponent = () => {
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoLoadError, setPhotoLoadError] = useState(false);
  
  // Extract the specific value we need to track
  const currentPhotoUrl = personnelData?.photoUrl;
  
  // Initialize photo URL from personnel data
  useEffect(() => {
    if (currentPhotoUrl) {
      const constructedUrl = constructPhotoUrl(currentPhotoUrl);
      setPhotoUrl(constructedUrl);
      setPhotoLoadError(false);
      console.log('Edit mode - Using photo URL:', constructedUrl);
    } else {
      setPhotoUrl(null);
      setPhotoLoadError(false);
    }
  }, [currentPhotoUrl]);


     const handlePhotoError = () => {
    console.log('Photo failed to load:', photoUrl);
    setPhotoLoadError(true);
  };

    
   const displayPhotoUrl = profilePhotoPreview || photoUrl;

  return (
    <div style={{
      width: '100px',
      height: '100px',
      borderRadius: '50%',
      backgroundColor: '#f3f4f6',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '3px solid #e5e7eb',
      overflow: 'hidden',
      position: 'relative',
      flexShrink: 0
    }}>
      {displayPhotoUrl && !photoLoadError ? (
        <>
          <img 
            src={displayPhotoUrl} 
            alt={`${formData.fullName || personnelData?.fullName} profile`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            onError={handlePhotoError}
            onLoad={() => console.log('Photo loaded successfully:', displayPhotoUrl)}
          />
          <div 
            title={profilePhotoPreview ? 'New photo selected' : `Photo loaded from: ${photoUrl}`}
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              width: '12px',
              height: '12px',
              backgroundColor: profilePhotoPreview ? '#f59e0b' : '#10b981',
              borderRadius: '50%',
              fontSize: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold'
            }}
          >
            {profilePhotoPreview ? '📷' : '✓'}
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: '40px',
            color: '#9ca3af',
            marginBottom: '4px'
          }}>
            👤
          </div>
          {personnelData?.photoUrl && photoLoadError && (
            <div 
              title={`Photo URL failed to load: ${photoUrl}`}
              style={{
                fontSize: '8px',
                color: '#ef4444',
                fontWeight: 'bold'
              }}
            >
              ⚠️
            </div>
          )}
        </div>
      )}
    </div>
  );
};

  // Prevent body scroll when modal is open
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (profilePhotoPreview) {
        URL.revokeObjectURL(profilePhotoPreview);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profilePhotoPreview]);

  if (!personnelData) {
    return (
      <div style={{ 
        padding: '60px 24px', 
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%'
      }}>
        <div>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h3 style={{ color: '#374151', margin: '0 0 8px 0' }}>Personnel Data Not Found</h3>
          <p style={{ color: '#6b7280', margin: 0 }}>Unable to load personnel details for editing.</p>
        </div>
      </div>
    );
  }

  // Replace the entire modal structure in PersonnelEditWrapper.tsx with this simpler approach
// This avoids complex flex layouts that might be causing issues

return (
  <div 
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    }}
    onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}
  >
    <div 
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '800px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        display: 'flex',
        flexDirection: 'column' as const,
        maxHeight: 'calc(100vh - 40px)',
        overflow: 'hidden',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header — fixed at top, never scrolls */}
      <div 
        style={{
          padding: '24px 24px 20px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: 'white',
          borderRadius: '12px 12px 0 0',
          flexShrink: 0,
        }}
      >
        <div>
          <h2 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: '600',
            color: '#111827'
          }}>
            Edit Personnel Details
          </h2>
          <p style={{
            margin: '4px 0 0 0',
            fontSize: '14px',
            color: '#6b7280'
          }}>
            Editing information for {personnelData.fullName}
          </p>
        </div>
        <button 
          type="button" 
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#6b7280',
            padding: '4px',
            borderRadius: '4px',
            transition: 'color 0.2s'
          }}
        >
          ✕
        </button>
      </div>

      {/* ─── Error Banner — fixed above scrollable area ─── */}
      {formError && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 32px',
          backgroundColor: '#fef2f2',
          borderBottom: '1px solid #fecaca',
          fontSize: '13px',
          fontWeight: 500,
          color: '#dc2626',
          fontFamily: "'Segoe UI', sans-serif",
          flexShrink: 0,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          <span style={{ flex: 1 }}>{formError}</span>
          <button
            type="button"
            onClick={() => setFormError(null)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#dc2626',
              fontSize: '18px',
              lineHeight: 1,
              padding: '0 4px',
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Content — scrollable area */}
      <div style={{ padding: '32px', flex: 1, overflowY: 'auto' as const }}>
        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          
          {/* Profile Photo Section */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '24px', 
            marginBottom: '32px',
            paddingBottom: '24px',
            borderBottom: '1px solid #f1f5f9'
          }}>
            <PhotoComponent />
            
            <div style={{ flex: 1 }}>
              <input
                type="file"
                id="profile-photo-edit"
                accept="image/*"
                onChange={handleProfilePhotoChange}
                style={{ display: 'none' }}
              />
              <label
                htmlFor="profile-photo-edit"
                style={{
                  display: 'inline-block',
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                  marginBottom: '12px'
                }}
              >
                Change Profile Photo
              </label>
              
              <div style={{
                fontSize: '12px',
                color: '#6b7280'
              }}>
                
                <p style={{ margin: 0 }}>
                  Created: {formatDate(personnelData.createdAt)}
                  {personnelData.updatedAt !== personnelData.createdAt && (
                    <span> • Last updated: {formatDate(personnelData.updatedAt)}</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* All your form sections here - Personal Info, Identity Documents, etc. */}
          {/* Personal Info Section */}
          <div style={{ marginBottom: '32px' }}>
            <h4 style={{ 
              margin: '0 0 16px 0', 
              color: '#111827',
              fontSize: '16px',
              fontWeight: '600',
              paddingBottom: '12px',
              borderBottom: '2px solid #f1f5f9'
            }}>
              Personal Information
            </h4>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '16px' }}>
              {renderEditableField('First Name', 'firstName', 'text', undefined, true)}
              {renderEditableField('Last Name', 'lastName', 'text', undefined, true)}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '16px' }}>
              {renderEditableField('Full Name', 'fullName', 'text', undefined, true)}
              {renderEditableField('Gender', 'sex', 'select', ['Male', 'Female'], true)}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '16px' }}>
              {renderEditableField('Date of Birth', 'dateOfBirth', 'date', undefined, true)}
              {renderEditableField('Nationality', 'nationality', 'text', undefined, true)}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              {renderEditableField('Place of Birth', 'placeOfBirth', 'text', undefined, true)}
              {renderEditableField('Blood Group', 'bloodGroup', 'select', ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'])}
            </div>
          </div>

          {/* Identity Documents Section */}
          <div style={{ marginBottom: '32px' }}>
            <h4 style={{ 
              margin: '0 0 16px 0', 
              color: '#111827',
              fontSize: '16px',
              fontWeight: '600',
              paddingBottom: '12px',
              borderBottom: '2px solid #f1f5f9'
            }}>
              Identity Documents
            </h4>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '16px' }}>
              {renderEditableField('National ID Number', 'nationalIdNo', 'text', undefined, true)}
              {renderEditableField('Passport Number', 'passportNumber', 'text', undefined, true)}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              {renderEditableField('Passport Issue Date', 'passportIssueDate', 'date', undefined, true)}
              {renderEditableField('Passport Expiry Date', 'passportExpiryDate', 'date', undefined, true)}
            </div>
          </div>

          {/* Visa Information Section */}
          <div style={{ marginBottom: '32px' }}>
            <h4 style={{ 
              margin: '0 0 16px 0', 
              color: '#111827',
              fontSize: '16px',
              fontWeight: '600',
              paddingBottom: '12px',
              borderBottom: '2px solid #f1f5f9'
            }}>
              Visa Information
            </h4>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '16px' }}>
              {renderEditableField('Visa Type', 'typeOfVisa', 'select', ['Tourist', 'Business', 'Work', 'Student', 'Transit', 'Resident', 'Other'], true)}
              {renderEditableField('Visa Issue Date', 'visaIssueDate', 'date', undefined, true)}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              {renderEditableField('Visa Expiry Date', 'visaExpiry', 'date', undefined, true)}
              <div>{/* Empty cell for alignment */}</div>
            </div>
          </div>

          {/* Documents Section */}
          {renderDocumentsSection()}

          

        </form>
      </div>

      {/* Footer — fixed at bottom, never scrolls */}
      <div style={{
        padding: '20px 32px',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '16px',
        backgroundColor: '#f9fafb',
        borderRadius: '0 0 12px 12px',
        flexShrink: 0,
      }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            padding: '12px 24px',
            border: '1px solid #d1d5db',
            backgroundColor: 'white',
            color: '#374151',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.2s',
            fontSize: '14px'
          }}
        >
          Cancel
        </button>
        
        <button
          onClick={handleSave}
          disabled={isSaving}
          style={{
            padding: '12px 24px',
            border: '1px solid #F3694A',
            backgroundColor: isSaving ? '#9ca3af' : '#F3694A',
            color: 'white',
            borderRadius: '8px',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            fontWeight: '500',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            minWidth: '140px',
            justifyContent: 'center'
          }}
        >
          {isSaving ? (
            <>
              <div 
                className="spinner"
                style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #ffffff',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%'
                }} 
              />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>
    </div>
  </div>
);
};

export default PersonnelEditWrapper;